/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React from 'react';
import styled from 'styled-components';
import {ActionButton} from 'screens/commons';
import IMAGES from 'constants/Images';

const BankDetails = ({
  confirmAccountNumber,
  onChangeHandler,
  onSubmit,
  formData,
  isLoading,
  isDisabled,
  isIfscValid,
  ifscResponse,
  isAccountNumberConfirmed,
  accountTypeText,
}) => (
  <BasicDetailsWrapper>
    <Container>
      <CardContainer>
        <CardContent>
          {accountTypeText && (
            <AlertBanner>
              <InfoImage src={IMAGES.infoIconImage} alt="alert" />
              <AlertText
                className="body-small"
                dangerouslySetInnerHTML={{
                  __html: accountTypeText,
                }}
              />
            </AlertBanner>
          )}
          <FormSection>
            <FormField>
              <FieldLabel>Account Number</FieldLabel>
              <InputField>
                <Input
                  name="beneficiaryAccountNo"
                  type="number"
                  value={formData.beneficiaryAccountNo}
                  onChange={onChangeHandler}
                  onBlur={confirmAccountNumber}
                />
                {isAccountNumberConfirmed && <VerifiedBadge>Verified</VerifiedBadge>}
              </InputField>
            </FormField>

            <FormField>
              <FieldLabel>IFSC Code</FieldLabel>
              <InputField>
                <Input
                  name="beneficiaryAccountIfsc"
                  value={formData?.beneficiaryAccountIfsc}
                  onChange={onChangeHandler}
                />
                {formData.beneficiaryAccountIfsc && isIfscValid && ifscResponse?.bank ? (
                  <VerifiedBadge>Valid</VerifiedBadge>
                ) : null}
              </InputField>
              {!isIfscValid ? (
                <ErrorMessage>Invalid IFSC Code, please check.</ErrorMessage>
              ) : ifscResponse?.bank ? (
                <FormField className="mt-3">
                  <FieldLabel>Bank Details</FieldLabel>
                  <BankDetailsContainer>
                    <BankDetailRow>
                      <BankDetailLabel>Bank</BankDetailLabel>
                      <BankDetailValue>{ifscResponse?.bank}</BankDetailValue>
                    </BankDetailRow>
                    <BankDetailRow>
                      <BankDetailLabel>Branch</BankDetailLabel>
                      <BankDetailValue>{ifscResponse?.branch}</BankDetailValue>
                    </BankDetailRow>
                    <BankDetailRow>
                      <BankDetailLabel>City</BankDetailLabel>
                      <BankDetailValue>
                        {ifscResponse?.city}, {ifscResponse?.state}
                      </BankDetailValue>
                    </BankDetailRow>
                    <BankDetailRow>
                      <BankDetailLabel>Address</BankDetailLabel>
                      <BankDetailValue className="address-value">
                        {ifscResponse?.address}
                      </BankDetailValue>
                    </BankDetailRow>
                  </BankDetailsContainer>
                </FormField>
              ) : null}
            </FormField>
          </FormSection>

          <CardFooter>
            <ActionButton
              isDisabled={isDisabled}
              buttonText="Verify & Continue"
              callback={onSubmit}
              isLoading={isLoading}
              className="mt-2"
            />
          </CardFooter>
        </CardContent>
      </CardContainer>
    </Container>
  </BasicDetailsWrapper>
);

const BasicDetailsWrapper = styled.div`
  height: 100%;
  width: 100%;
  //TODO: width:max-content;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.background.primary};
`;

const Container = styled.div`
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 1.5rem;
`;

const CardContainer = styled.div`
  overflow: hidden;
`;

const CardContent = styled.div`
  padding: 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoImage = styled.img`
  height: 1.25rem;
  width: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.secondary};
`;

const InputField = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) => props.theme.colors.background.primary};
  border: 1px solid ${(props) => props.theme.colors.border?.default || '#DDDDDD'};
  border-radius: ${(props) => props.theme.borderRadius?.md || '6px'};
  padding: 0.75rem;
`;

const Input = styled.input.attrs({
  className: 'body-regular',
})`
  border: none;
  background: transparent;
  outline: none;
  color: ${(props) => props.theme.colors.text.primary};
  width: 100%;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const VerifiedBadge = styled.span`
  background: ${(props) => props.theme.colors.feedback.successLight || '#dcfce7'};
  color: ${(props) => props.theme.colors.feedback.success || '#15803d'};
  padding: 0.25rem 0.5rem;
  border-radius: ${(props) => props.theme.borderRadius?.sm || '4px'};
  font-size: 0.75rem;
  margin-left: 0.75rem;
`;

const ErrorMessage = styled.p`
  color: ${(props) => props.theme.colors.feedback.error};
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

const BankDetailsContainer = styled.div`
  background: ${(props) => props.theme.colors.background.secondary || '#f8fafc'};
  border: 1px solid ${(props) => props.theme.colors.border?.default || '#DDDDDD'};
  border-radius: ${(props) => props.theme.borderRadius?.md || '6px'};
  padding: 0.5rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.text.secondary};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BankDetailRow = styled.div`
  display: flex;
  padding: 0.5rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.border?.light || '#F5F5F5'};

  &:last-child {
    border-bottom: none;
  }
`;

const BankDetailLabel = styled.span`
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.secondary};
  flex: 0 0 80px;
`;

const BankDetailValue = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  font-weight: ${(props) => props.theme.typography.fontWeight.regular};
  flex: 1;

  &.address-value {
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
    white-space: pre-wrap;
  }
`;

const AlertBanner = styled.div`
  background: ${(props) => props.theme.colors.brand.secondary};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const AlertText = styled.p`
  color: ${(props) => props.theme.colors.brand.primary || '#6b21a8'};
  margin: 0;
  mark {
    background: none;
    font-weight: ${(props) => props.theme.typography.fontWeight.semiBold} !important;
    color: ${(props) => props.theme.colors.brand.primary || '#6b21a8'};
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default BankDetails;
